import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { BarChart3, TrendingUp, AlertTriangle, ShieldCheck, GraduationCap, GitBranch, ClipboardList, FileText, AlertOctagon, MessageSquareWarning, Users, RefreshCw } from "lucide-react";
import logger from "@/lib/logger";

const HEALTH_META = {
  green: { label: "On track", color: "#059669", bg: "#D1FAE5" },
  amber: { label: "Attention needed", color: "#D97706", bg: "#FEF3C7" },
  red:   { label: "Action required", color: "#DC2626", bg: "#FEE2E2" },
};

function KpiTile({ testId, icon: Icon, title, value, sub, tone = "neutral" }) {
  const tones = {
    neutral: "text-slate-900",
    good:    "text-emerald-600",
    warn:    "text-amber-600",
    bad:     "text-rose-600",
  };
  return (
    <div className="vq-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2 text-slate-500">
        <Icon className="w-4 h-4" />
        <div className="vq-overline text-[10px]">{title}</div>
      </div>
      <div className={`font-display font-extrabold text-3xl ${tones[tone]}`} data-testid={`${testId}-value`}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Meter({ pct, testId, label }) {
  const p = Math.max(0, Math.min(100, Number(pct || 0)));
  const color = p >= 80 ? "#059669" : p >= 50 ? "#D97706" : "#DC2626";
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
        <span>{label}</span>
        <span className="font-mono font-semibold" data-testid={`${testId}-pct`}>{p.toFixed(1)}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function QualityKPIs() {
  const { loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("all");
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    api.get("/devices").then(({ data }) => setProjects(data || []))
      .catch((e) => logger.warn("Load projects:", e?.response?.status));
  }, [authLoading]);

  const load = useCallback(() => {
    setLoading(true);
    const q = pid && pid !== "all" ? `?project_id=${encodeURIComponent(pid)}` : "";
    api.get(`/quality/kpis${q}`)
      .then(({ data }) => setKpis(data))
      .catch((e) => logger.warn("KPIs:", e?.response?.status))
      .finally(() => setLoading(false));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const health = kpis ? (HEALTH_META[kpis.kpi_health] || HEALTH_META.amber) : null;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6">
          <div className="vq-overline mb-2 flex items-center gap-2"><BarChart3 className="w-3 h-3" /> Quality KPIs</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
            Quality KPI Dashboard
          </h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            Operational trust signals for the quality team — CAPA discipline, deviation load, training
            compliance, risk control coverage and traceability. Assistive metrics only; a qualified reviewer
            must interpret them in context.
          </p>
        </div>

        <div className="mb-6 flex items-end gap-3 flex-wrap">
          <div className="max-w-sm w-64">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Project scope</Label>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="kpi-project-select"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="kpi-project-all">All projects (portfolio)</SelectItem>
                {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id} data-testid={`kpi-project-${p.project_id}`}>{p.device_name || p.project_id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="kpi-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {loading && <div className="text-sm text-slate-500" data-testid="kpi-loading">Computing…</div>}

        {kpis && (
          <>
            <div className="vq-card p-6 mb-6" data-testid="kpi-health-panel">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm"
                     style={{ backgroundColor: health.bg, color: health.color }}
                     data-testid="kpi-health-badge">
                  <TrendingUp className="w-4 h-4" /> {health.label}
                </div>
                <div className="text-xs text-slate-500 font-mono">
                  Snapshot {new Date(kpis.as_of).toLocaleString()}
                </div>
                <div className="ml-auto text-xs text-slate-500 max-w-md text-right">
                  {kpis.disclaimer}
                </div>
              </div>
            </div>

            {/* CAPA + Deviations + Changes + Complaints */}
            <h2 className="font-display font-bold text-lg text-slate-900 mb-3">Quality events</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <KpiTile testId="kpi-capa-open" icon={ClipboardList} title="CAPA open"
                       value={kpis.capa.open}
                       sub={`${kpis.capa.total} total, ${kpis.capa.closed} closed`}
                       tone={kpis.capa.open > 0 ? "warn" : "good"} />
              <KpiTile testId="kpi-capa-overdue" icon={AlertTriangle} title="CAPA overdue actions"
                       value={kpis.capa.overdue_actions}
                       sub="Action past due date & still open"
                       tone={kpis.capa.overdue_actions > 0 ? "bad" : "good"} />
              <KpiTile testId="kpi-capa-ontime" icon={ShieldCheck} title="CAPA on-time closure"
                       value={`${kpis.capa.on_time_closure_pct}%`}
                       sub="Closed within 90 days"
                       tone={kpis.capa.on_time_closure_pct >= 80 ? "good" : kpis.capa.on_time_closure_pct >= 60 ? "warn" : "bad"} />
              <KpiTile testId="kpi-capa-effectiveness" icon={TrendingUp} title="CAPA effectiveness rate"
                       value={`${kpis.capa.effectiveness_rate_pct}%`}
                       sub={`${kpis.capa.effectiveness_effective} effective / ${kpis.capa.effectiveness_partial} partial / ${kpis.capa.effectiveness_not_effective} not effective`}
                       tone={kpis.capa.effectiveness_rate_pct >= 80 ? "good" : "warn"} />
              <KpiTile testId="kpi-deviations-open" icon={AlertOctagon} title="Deviations open"
                       value={kpis.deviations.open}
                       sub={`${kpis.deviations.high_open} high/critical`}
                       tone={kpis.deviations.high_open > 0 ? "bad" : kpis.deviations.open > 0 ? "warn" : "good"} />
              <KpiTile testId="kpi-changes-open" icon={GitBranch} title="Changes open"
                       value={kpis.changes.open}
                       sub={`${kpis.changes.closed_30d} closed in 30 days`}
                       tone={kpis.changes.open > 0 ? "warn" : "good"} />
              <KpiTile testId="kpi-complaints-open" icon={MessageSquareWarning} title="Complaints open"
                       value={kpis.complaints.open}
                       sub={kpis.complaints.cycle_days_avg !== null ? `${kpis.complaints.cycle_days_avg} d avg cycle` : "No closed complaints"}
                       tone={kpis.complaints.open > 0 ? "warn" : "good"} />
              <KpiTile testId="kpi-complaints-linked" icon={ClipboardList} title="Complaints → CAPA"
                       value={kpis.complaints.linked_to_capa}
                       sub={`of ${kpis.complaints.total} total`}
                       tone="neutral" />
            </div>

            {/* Risk + Training + Documents */}
            <h2 className="font-display font-bold text-lg text-slate-900 mb-3">Risk, training, documents</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="vq-card p-5" data-testid="kpi-risk-panel">
                <div className="flex items-center gap-2 mb-3 text-slate-500">
                  <AlertTriangle className="w-4 h-4" />
                  <div className="vq-overline text-[10px]">Risk (ISO 14971)</div>
                </div>
                <div className="font-display font-extrabold text-3xl text-slate-900" data-testid="kpi-risk-total">
                  {kpis.risks.total}
                </div>
                <div className="text-xs text-slate-500 mb-4">
                  <span data-testid="kpi-risk-high">{kpis.risks.high_residual}</span> above benefit-risk threshold
                </div>
                <div className="space-y-3">
                  <Meter testId="kpi-risk-control-meter" label="Risks with control described" pct={kpis.risks.with_control_pct} />
                  <Meter testId="kpi-risk-verif-meter" label="Risks with verification evidence" pct={kpis.risks.with_verification_pct} />
                </div>
              </div>

              <div className="vq-card p-5" data-testid="kpi-training-panel">
                <div className="flex items-center gap-2 mb-3 text-slate-500">
                  <GraduationCap className="w-4 h-4" />
                  <div className="vq-overline text-[10px]">Training (ISO 13485 §6.2)</div>
                </div>
                <div className="font-display font-extrabold text-3xl text-slate-900" data-testid="kpi-training-compliance">
                  {kpis.trainings.compliance_pct}%
                </div>
                <div className="text-xs text-slate-500 mb-4">
                  <span data-testid="kpi-training-overdue">{kpis.trainings.overdue}</span> overdue •{" "}
                  {kpis.trainings.completed}/{kpis.trainings.completed + kpis.trainings.assigned} completed
                </div>
                <Meter testId="kpi-training-meter" label="Completion rate" pct={kpis.trainings.compliance_pct} />
              </div>

              <div className="vq-card p-5" data-testid="kpi-documents-panel">
                <div className="flex items-center gap-2 mb-3 text-slate-500">
                  <FileText className="w-4 h-4" />
                  <div className="vq-overline text-[10px]">Documents</div>
                </div>
                <div className="font-display font-extrabold text-3xl text-slate-900" data-testid="kpi-documents-effective-pct">
                  {kpis.documents.effective_pct}%
                </div>
                <div className="text-xs text-slate-500 mb-4">
                  {kpis.documents.effective} effective of {kpis.documents.total}
                </div>
                <Meter testId="kpi-documents-meter" label="Documents effective" pct={kpis.documents.effective_pct} />
              </div>
            </div>

            {/* Traceability */}
            <h2 className="font-display font-bold text-lg text-slate-900 mb-3">Traceability coverage</h2>
            <div className="vq-card p-6 mb-6" data-testid="kpi-trace-panel">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Meter testId="kpi-trace-risks" label={`Risks with mitigating control (${kpis.traceability.risks_total ?? 0} risks)`}
                       pct={kpis.traceability.risks_with_control_pct} />
                <Meter testId="kpi-trace-controls" label={`Controls with verification link (${kpis.traceability.controls_total ?? 0} controls)`}
                       pct={kpis.traceability.controls_with_verification_pct} />
                <Meter testId="kpi-trace-reqs" label={`Requirements with evidence link (${kpis.traceability.requirements_total ?? 0} requirements)`}
                       pct={kpis.traceability.requirements_with_evidence_pct} />
                <Meter testId="kpi-trace-sections" label={`Submission sections with evidence (${kpis.traceability.submission_sections_total ?? 0} sections)`}
                       pct={kpis.traceability.submission_sections_with_evidence_pct} />
              </div>
              <div className="text-xs text-slate-500 mt-4" data-testid="kpi-trace-total">
                Total traceability links recorded: {kpis.traceability.total_links}
              </div>
              {kpis.traceability.note && (
                <div className="text-xs text-slate-500 mt-1 italic">{kpis.traceability.note}</div>
              )}
            </div>

            {/* Management review */}
            <div className="vq-card p-6" data-testid="kpi-mr-panel">
              <div className="flex items-center gap-2 mb-3 text-slate-500">
                <Users className="w-4 h-4" />
                <div className="vq-overline text-[10px]">Management Review (ISO 13485 §5.6)</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Last held</div>
                  <div className="font-semibold text-slate-900" data-testid="kpi-mr-last">
                    {kpis.management_review.last_held ? new Date(kpis.management_review.last_held).toLocaleDateString() : "Never recorded"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Next due</div>
                  <div className="font-semibold text-slate-900" data-testid="kpi-mr-next">
                    {kpis.management_review.next_due ? new Date(kpis.management_review.next_due).toLocaleDateString() : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Status</div>
                  <div className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${kpis.management_review.overdue ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}
                       data-testid="kpi-mr-status">
                    {kpis.management_review.overdue ? "Overdue / never held" : "Current"}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
